import React from 'react';
import {
  MdOutlineRadioButtonUnchecked,
  MdOutlineStarBorder,
  MdOutlineStar,
  MdOutlineCheckBoxOutlineBlank
} from 'react-icons/md';

import { Flex, Icon, LssProp, Text } from '@lucidhq/lucidium';

import { TemplateQuestion } from '~/components/Shared/hooks/useFetchTemplateQuestions';
import { EnhancedTemplateQuestion } from '~/utils/templateQuestions';

import { useBehavioralQuestions } from '../BehavioralQuestionsStep/BehavioralQuestionsProvider';
import { useStandardQuestionsProvider } from '../StandardQuestionsStep/StandardQuestionsProvider';
import { AnchoredAnswerItem } from './AnchoredAnswerItem';
import { QUESTION_LOGIC_ITEMS } from './QuestionLogicDrawer';

export const updateInTargetAnswers = (
  targetOptionArr: number[],
  index: number
): number[] => {
  const copy = [...targetOptionArr];

  if (copy.includes(index)) {
    const deleteIdx = copy.indexOf(index);
    copy.splice(deleteIdx, 1);
  } else {
    copy.push(index);
  }

  // NOTE: if there are no in target options populate the copy with the in-target-brand
  // Otherwise sort the numerical options
  if (!copy.length) {
    copy.push(0);
  } else {
    copy.sort((a: number, b: number) => a - b);
  }

  return copy;
};

const getAnchoredAnswerKey = (
  answer: string | null,
  question: EnhancedTemplateQuestion
) => {
  if (answer === question?.other_answer) {
    return QUESTION_LOGIC_ITEMS.OTHER;
  }
  if (answer === question?.none_of_the_above_answer) {
    return QUESTION_LOGIC_ITEMS.NOTA;
  }
  return '';
};

const getMappedAnswers = (
  answers: (string | null)[],
  question: EnhancedTemplateQuestion
) => {
  return answers.map((answer: string | null) => {
    const anchoredAnswerKey = getAnchoredAnswerKey(answer, question);
    const isAnchoredAnswer =
      answer === question?.none_of_the_above_answer ||
      answer === question?.other_answer;

    return {
      value: answer,
      isAnchoredAnswer,
      anchoredAnswerKey
    };
  });
};

const targetAnswerWrapperLss: LssProp = {
  display: 'flex',
  alignItems: 'center',
  p: 'xs',
  borderRadius: 'xl'
};

const answerOptionLss: LssProp = {
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px'
};

const optionsListLss: LssProp = {
  flexDirection: 'column',
  gap: '4px',
  listStyle: 'none',
  p: 'none'
};

const anchoredAnswerOptionLss: LssProp = {
  ...answerOptionLss,
  justifyContent: 'center',
  paddingLeft: '0.25rem',
  marginLeft: 'none'
};

type NonGridAnswerOptionsProps = {
  question: EnhancedTemplateQuestion;
  onSave: (data: Partial<TemplateQuestion>) => void;
  readOnly?: boolean;
  hasNoneOfTheAbove: boolean;
};

const NONE_OF_THE_ABOVE = 'None of the above';

export const NonGridAnswerOptions = ({
  question,
  onSave,
  readOnly = false,
  hasNoneOfTheAbove
}: NonGridAnswerOptionsProps) => {
  //NOTE: this value is a reference to the NOTA value for both standard and behavioral questions.
  const { translatedNOTAValue = '' } = useStandardQuestionsProvider();
  const { referenceNOTAValue = '' } = useBehavioralQuestions();

  const answerOptionIcon =
    question.question_type === 'radio'
      ? MdOutlineRadioButtonUnchecked
      : MdOutlineCheckBoxOutlineBlank;

  const hasFoundNOTA = !!question?.answers?.find(
    answer => answer === question?.none_of_the_above_answer
  );
  //NOTE: this value is needed to validate if the NOTA value should be added to the list of default answers
  const isNOTADefault = !!(
    question?.none_of_the_above_answer === NONE_OF_THE_ABOVE ||
    question?.none_of_the_above_answer === translatedNOTAValue ||
    question?.none_of_the_above_answer === referenceNOTAValue
  );

  const answersCopy =
    hasNoneOfTheAbove && !hasFoundNOTA && isNOTADefault
      ? [...question?.answers, question?.none_of_the_above_answer]
      : [...question?.answers];

  const mappedAnswers = getMappedAnswers(answersCopy, question);
  const otherIndex = mappedAnswers.findIndex(
    answer => answer.anchoredAnswerKey === QUESTION_LOGIC_ITEMS.OTHER
  );
  const notaIndex = mappedAnswers.findIndex(
    answer => answer.anchoredAnswerKey === QUESTION_LOGIC_ITEMS.NOTA
  );

  const isTargetAnswerOptionVisible =
    !!question?.meta?.isStandard ||
    (question.kpi_type === 'custom' && !question?.isBehavioralCustomKPI);

  const isBehavioralKpi = !!(
    question.meta.isBehavioral || question?.isBehavioralCustomKPI
  );

  return (
    <Flex as={'ul'} lss={optionsListLss}>
      {mappedAnswers?.map((answer: any, index: number) => {
        const isSelected = !!question?.multi_target_option_index?.includes(
          index
        );
        const notaAnswer =
          question?.none_of_the_above_answer ?? NONE_OF_THE_ABOVE;
        const isAnswerNOTA =
          answer.value === notaAnswer || answer.value === NONE_OF_THE_ABOVE;

        // NOTE: the NOTA answer type can be either a string or null.
        // additionally we only want to show customized options on standard and behavioral questions at this time.
        if (
          (!hasNoneOfTheAbove && isAnswerNOTA) ||
          (answer.isAnchoredAnswer &&
            (!question.meta.isCustomKPI ||
              (question.meta.isCustomKPI && !question.use_answer_meta)))
        ) {
          return null;
        }

        return (
          <Flex
            as="li"
            key={`${index}-${answer.value}`}
            lss={{
              ...answerOptionLss,
              ...(answer.isAnchoredAnswer &&
                !question.meta.isCustomKPI && {
                  gap: '0.325rem',
                  mb: 'xs'
                }),
              paddingLeft:
                answer.isAnchoredAnswer &&
                question.meta.isCustomKPI &&
                !question.isBehavioralCustomKPI
                  ? '2.625rem'
                  : '0.25rem'
            }}
          >
            {!answer.isAnchoredAnswer && isTargetAnswerOptionVisible && (
              <Flex
                as="a"
                href="#"
                role="button"
                lss={{
                  ...targetAnswerWrapperLss,
                  ...(!isSelected && readOnly && { visibility: 'hidden' }),
                  ...(isSelected && readOnly && { cursor: 'default' })
                }}
                onClick={(e: React.SyntheticEvent) => {
                  e.preventDefault();
                  onSave({
                    multi_target_option_index: updateInTargetAnswers(
                      question.multi_target_option_index,
                      index
                    )
                  });
                }}
              >
                <Icon
                  as={isSelected ? MdOutlineStar : MdOutlineStarBorder}
                  size={22}
                  lss={{ color: 'secondary500' }}
                />
              </Flex>
            )}
            <>
              <Icon as={answerOptionIcon} size={16} />
              <Text lss={{ ml: 'xs', flex: 1 }}>{answer.value}</Text>
            </>
          </Flex>
        );
      })}
      {/* Show both other and NOTA if the value is not null and question is not custom and use answer meta is false   */}
      {!!question?.other_answer &&
        (!question.meta.isCustomKPI ||
          (question.meta.isCustomKPI && !question.use_answer_meta)) && (
          <Flex
            as="li"
            lss={{
              ...anchoredAnswerOptionLss,
              mb: 'sm',
              ...(isBehavioralKpi && {
                marginLeft: '-2.4rem'
              })
            }}
          >
            <AnchoredAnswerItem
              answerOptionIcon={answerOptionIcon}
              value={question.other_answer}
              answers={answersCopy}
              anchoredAnswerKey={QUESTION_LOGIC_ITEMS.OTHER}
              onSave={onSave}
              index={otherIndex}
              readOnly={readOnly}
            />
          </Flex>
        )}
      {!!question?.none_of_the_above_answer &&
        (!question.meta.isCustomKPI ||
          (question.meta.isCustomKPI && !question.use_answer_meta)) && (
          <Flex
            as="li"
            lss={{
              ...anchoredAnswerOptionLss,
              ...(isBehavioralKpi && {
                marginLeft: '-2.4rem'
              })
            }}
          >
            <AnchoredAnswerItem
              answerOptionIcon={answerOptionIcon}
              value={question?.none_of_the_above_answer}
              answers={answersCopy}
              anchoredAnswerKey={QUESTION_LOGIC_ITEMS.NOTA}
              onSave={onSave}
              index={notaIndex}
              readOnly={readOnly}
            />
          </Flex>
        )}
    </Flex>
  );
};
